import * as R from 'ramda'
import * as React from 'react'
import type {RouteComponentProps, RouteProps} from 'react-router-dom'
import {Route} from 'react-router-dom'
import type {OmitStrict} from 'type-zoo'
import type AuthenticatedPlayer from '~/models/app/AuthenticatedPlayer'
import type {TNotSignedInYetReferrerPayload} from '~/pages/Auth/SignIn'
import {useSelector} from '~/redux/hooks'
import {Redirect} from '~/routing/ReactRouterLinks'
import {ROUTE_TYPE_KEY} from '~/routing/Route/types'
import {staticURLs} from '~/routing/sitemap'

export interface ISignedInRouteComponentProps<
  DynamicPathPieces extends {
    [K in keyof DynamicPathPieces]?: string | undefined
  } = void,
>
  extends RouteComponentProps<DynamicPathPieces>, ExtraProps {}

interface PropsFromRouter extends OmitStrict<RouteProps, 'component'> {
  component: React.ComponentType<ISignedInRouteComponentProps<any>>
}

interface IProps extends PropsFromRouter {}

const ROUTE_TYPE = 'SignedInRoute'

// all SignedIn routes get these props, extending what RouteComponentProps provides
export interface IPropsGuaranteedBySignedInRoutes {
  authenticatedPlayer: AuthenticatedPlayer
}
// IPropsGuaranteedBySignedInRoutes with the ROUTE_TYPE thing added to it for internal use
interface ExtraProps extends IPropsGuaranteedBySignedInRoutes {
  // This is what gets the TS compiler to check that we've used the correct Route/RouteComponent pairing in routes.tsx
  // The props type of `this.props.component` MUST match this interface, which requires that this special key/value
  // pair exists, with the correct value. (the value is `typeof ROUTE_TYPE` which is `'SignedInRoute'` [not `string`])
  // If a route component's props don't extend this (via ISignedInRouteComponentProps), the types won't match
  // and the compiler will complain.
  [ROUTE_TYPE_KEY]: typeof ROUTE_TYPE
}

const SignedIn = ({component, ...routeProps}: IProps) => {
  const authenticatedPlayer = useSelector(
    s => s.authenticatedPlayerState.authenticatedPlayer
  )

  if (!authenticatedPlayer) {
    const payload: TNotSignedInYetReferrerPayload = {
      referrer: routeProps.location,
    }

    console.warn(
      'Not logged in yet. Redirecting to log in. Referrer: ',
      payload.referrer
    )
    return (
      <Redirect
        to={{
          pathname: staticURLs.auth.signin,
          state: payload,
        }}
      />
    )
  }

  return (
    <Route
      {...routeProps}
      render={routeComponentProps => {
        return React.createElement<ISignedInRouteComponentProps<any>>(
          // the cast here is necessary for the above `| SimpleRouteComponentType` to be allowed -
          // we want to allow components that don't need a guaranteed `player` but are still using `Route.SignedIn`
          // to have a simple constructor / type signature. Without this cast, every `Route.SignedIn` would have
          // to declare in its props that it takes `ISignedInRouteComponentProps`.
          component as React.ComponentType<ISignedInRouteComponentProps>,
          {
            ...routeComponentProps,
            authenticatedPlayer,
            [ROUTE_TYPE_KEY]: ROUTE_TYPE,
          }
        )
      }}
    />
  )
}

export default SignedIn

// since we sometimes need to pass the modified RouteComponentProps down to sub-components
// this helper can filter out all the other props a RouteComponent might have (say, from redux)
// without needing a large {redux1, ..., reduxN, ...routeComponentProps} rest operator
// (we could also try implementing a custom withRouter, but this works for now)
export const isolateSignedInRouteComponentProps = <
  DynamicPathPieces extends {[K in keyof DynamicPathPieces]?: string},
>(
  props: ISignedInRouteComponentProps<DynamicPathPieces>
): ISignedInRouteComponentProps<DynamicPathPieces> => {
  return R.pick(
    [
      ROUTE_TYPE_KEY,
      'authenticatedPlayer',
      'history',
      'match',
      'location',
      'staticContext',
    ],
    props
  )
}
